import { create } from 'zustand'
import {
  persistPendingInitialPasswordSetup,
  readPasswordSetupDeadlineMs,
  readPendingInitialPasswordSetup,
} from '@/features/auth/lib/initial-password-setup'
import { SP_PASSWORD_SETUP_REQUIRED_SESSION_KEY } from '@/features/auth/lib/constants'

const SP_FORCE_RESET_SESSION_KEY = 'brushfire_sp_requires_force_reset'

function readPersistedForceReset(): boolean {
  if (globalThis.sessionStorage === undefined) return false
  try {
    return globalThis.sessionStorage.getItem(SP_FORCE_RESET_SESSION_KEY) === '1'
  } catch {
    return false
  }
}

function persistForceReset(required: boolean) {
  if (globalThis.sessionStorage === undefined) return
  try {
    if (required) {
      globalThis.sessionStorage.setItem(SP_FORCE_RESET_SESSION_KEY, '1')
    } else {
      globalThis.sessionStorage.removeItem(SP_FORCE_RESET_SESSION_KEY)
    }
  } catch {
    /* private mode / quota */
  }
}

export type ProviderRole = 'SIGNAL_PROVIDER'

function readPersistedPasswordSetupRequired(): boolean {
  if (globalThis.sessionStorage === undefined) return false
  try {
    return (
      globalThis.sessionStorage.getItem(SP_PASSWORD_SETUP_REQUIRED_SESSION_KEY) ===
      '1'
    )
  } catch {
    return false
  }
}

function persistPasswordSetupRequired(required: boolean) {
  if (globalThis.sessionStorage === undefined) return
  try {
    if (required) {
      globalThis.sessionStorage.setItem(SP_PASSWORD_SETUP_REQUIRED_SESSION_KEY, '1')
    } else {
      globalThis.sessionStorage.removeItem(SP_PASSWORD_SETUP_REQUIRED_SESSION_KEY)
    }
  } catch {
    /* private mode / quota */
  }
}

interface User {
  id: string
  email: string
  name: string
  role: ProviderRole
  /** Tenant DNS label from identity `/me`; used for Socket.io auth. */
  subdomain: string | null
  /** BFP-1120 — SMS web survey guest session (survey before account). */
  guest?: boolean
  /** BFP-1131 — public share pending JWT (`pending:{sessionId}`). */
  surveyPending?: boolean
  /** BFP-1128 — from identity `/me`; drives Create Account dropdown + Home banner. */
  webSurveyRegistrationTypeEnabled?: boolean
}

interface SessionState {
  user: User | null
  isAuthenticated: boolean
  explicitLogout: boolean
  /** True when session deadline passed — user must complete /set-password. */
  passwordSetupRequired: boolean
  /** After signup verify without password — home allowed until deadline. */
  pendingInitialPasswordSetup: boolean
  passwordSetupDeadlineMs: number | null
  /** BFP-264 — True for migrated SP users who must complete /force-reset/password. */
  requiresForceReset: boolean
  setUser: (user: User | null) => void
  setPasswordSetupRequired: (required: boolean) => void
  markPendingInitialPasswordSetup: (expiresInSeconds: number) => void
  clearPendingInitialPasswordSetup: () => void
  setRequiresForceReset: (required: boolean) => void
  clearSession: () => void
}

export const useSessionStore = create<SessionState>((set) => ({
  user: null,
  isAuthenticated: false,
  explicitLogout: false,
  passwordSetupRequired: readPersistedPasswordSetupRequired(),
  pendingInitialPasswordSetup: readPendingInitialPasswordSetup(),
  passwordSetupDeadlineMs: readPasswordSetupDeadlineMs(),
  requiresForceReset: readPersistedForceReset(),
  setUser: (user) =>
    set({
      user,
      isAuthenticated: !!user,
      explicitLogout: false,
    }),
  setPasswordSetupRequired: (passwordSetupRequired) => {
    persistPasswordSetupRequired(passwordSetupRequired)
    set({ passwordSetupRequired })
  },
  markPendingInitialPasswordSetup: (expiresInSeconds) => {
    const safeSeconds = Math.max(1, Math.floor(expiresInSeconds))
    const deadlineMs = Date.now() + safeSeconds * 1000
    persistPendingInitialPasswordSetup(true, deadlineMs)
    set({
      pendingInitialPasswordSetup: true,
      passwordSetupDeadlineMs: deadlineMs,
      passwordSetupRequired: false,
    })
    persistPasswordSetupRequired(false)
  },
  clearPendingInitialPasswordSetup: () => {
    persistPendingInitialPasswordSetup(false, null)
    set({
      pendingInitialPasswordSetup: false,
      passwordSetupDeadlineMs: null,
      passwordSetupRequired: false,
    })
    persistPasswordSetupRequired(false)
  },
  setRequiresForceReset: (required) => {
    persistForceReset(required)
    set({ requiresForceReset: required })
  },
  clearSession: () => {
    persistPendingInitialPasswordSetup(false, null)
    persistPasswordSetupRequired(false)
    persistForceReset(false)
    set({
      user: null,
      isAuthenticated: false,
      explicitLogout: true,
      passwordSetupRequired: false,
      pendingInitialPasswordSetup: false,
      passwordSetupDeadlineMs: null,
      requiresForceReset: false,
    })
  },
}))
